import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import type { SystemDocumentPageEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentPageSchemas'
import { parseSystemDocumentPageEntries } from 'features/knowledgeDocumentViewer/types/systemDocumentPageSchemas'
import {
  systemDocumentPageByPageId,
  systemDocumentPageByPageNum,
} from 'features/knowledgeDocumentViewer/utils/documentEntryLookups'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { useMemo } from 'react'

export type UseSystemDocumentPageByNumArgs = {
  systemDocumentEntryId: string | undefined
  bqDocumentId: string | undefined
  pageNum: number | undefined
  enabled?: boolean
}

export function useSystemDocumentPageByNum({
  systemDocumentEntryId,
  bqDocumentId,
  pageNum,
  enabled = true,
}: UseSystemDocumentPageByNumArgs): {
  page: SystemDocumentPageEntry | undefined
  isLoading: boolean
  error: Error | undefined
  retry: () => void
} {
  const { siteID } = useKnowledgeDocumentDataContext()
  const { getPipelineConfigScopedTags, isPipelineConfigScopeLoading } =
    usePipelineConfig()

  const loadOptions = useMemo(() => {
    if (
      !enabled ||
      isPipelineConfigScopeLoading ||
      pageNum === undefined ||
      pageNum < 1
    ) {
      return undefined
    }

    const scopedTags = getPipelineConfigScopedTags({ site: siteID })
    if (!scopedTags) return undefined

    // system-document-page metadata is stored under UUID4 entry ids — look
    // up by indexed page_id. Cantilever thumbnail / 6400px hosts are UUID5.
    if (bqDocumentId) {
      return systemDocumentPageByPageId(bqDocumentId, pageNum, scopedTags)
    }

    if (!systemDocumentEntryId) return undefined
    return systemDocumentPageByPageNum(
      systemDocumentEntryId,
      pageNum,
      scopedTags
    )
  }, [
    bqDocumentId,
    enabled,
    getPipelineConfigScopedTags,
    isPipelineConfigScopeLoading,
    pageNum,
    siteID,
    systemDocumentEntryId,
  ])

  const {
    data: rawPages,
    isLoading,
    error,
    mutate,
  } = useGetRolodexEntriesByLoadOptions<unknown>(loadOptions, {
    keepPreviousData: false,
  })

  const page = useMemo(() => {
    if (rawPages === undefined) return undefined
    return parseSystemDocumentPageEntries(rawPages)[0]
  }, [rawPages])

  return {
    page: enabled && loadOptions ? page : undefined,
    error: enabled && loadOptions ? error : undefined,
    retry: (): void => {
      void mutate()
    },
    isLoading: enabled && loadOptions !== undefined && isLoading,
  }
}
